import React from "react";
import axios from "axios";
import { Badge, Button, Tabs, Plus, InfoCircle, AlertCircle, CheckCircle } from "@opendate-app/setlist";
import { useSeriesEditContext } from "../SeriesEditApp";
import StepFooter from "./StepFooter";
import TicketTypeDrawer from "./TicketTypeDrawer";
import TicketTemplatePicker from "./TicketTemplatePicker";
import SaveTicketTemplateModal from "./SaveTicketTemplateModal";
import AddOnsTab from "./AddOnsTab";
import PromoCodesTab from "./PromoCodesTab";
import DeliveryTab from "./DeliveryTab";
import MembershipsTab from "./MembershipsTab";
import SettingsTab from "./SettingsTab";
import SeriesFeeSettings from "./SeriesFeeSettings";
import SeatingChartPicker from "./SeatingChartPicker";
import HoldsTab from "./HoldsTab";
import KillsTab from "./KillsTab";
import SeriesTicketList from "./SeriesTicketList";
import TicketSetTabs from "./TicketSetTabs";
import TicketSetSettings from "./TicketSetSettings";
import DateCoverageCalendar from "./DateCoverageCalendar";
import WeeklyEntryTimes from "./WeeklyEntryTimes";
import PublishedLock, { PublishedLockNote } from "./PublishedLock";
import { followTicketName } from "../../ConfirmTicketing/utils/presaleWindows";
import { toastError } from "../../../shared/toastHelper";
import {
  blankTicketType,
  configFor,
  showTypesFor,
  allTicketTypes,
  inventoryLabel,
  capacitySummary,
  settingsFor,
  renameReferences,
  emptiedByRemoval,
  deletionBlockedMessage,
  replaceTicketTypes,
  sectionTicketTypes,
  withSeatingMode,
  withAddOnChart,
  onSaleLabel,
  chartContextOf,
  ADD_ON_CHART,
  swapTicketReferences,
  SALES_WINDOWS,
  copyName,
  moveRow,
  blankPresaleWindow,
  templateSnapshot,
  applyTemplateConfig,
  MAX_PLANNED_TICKET_TYPES,
  withTicketTemplate,
  withSetTicketTypes,
  withoutTicketType,
  withoutTicketSetConfig,
  ticketTypeReferences,
} from "../scheduleBuilder";
import {
  clearDecision,
  coverage,
  decideOverlap,
  decisionsOf,
  newTicketSet,
  ticketSetKeyOf,
  ticketSetsOf,
  typesInSet,
} from "../ticketSets";

const SEATING_MODES = [
  { value: "seated", label: "Seated" },
  { value: "general_admission", label: "General Admission" },
];

const subTabs = ({ canAccessMemberships }) => [
  { value: "admission", label: "Admission" },
  { value: "add_ons", label: "Add-ons" },
  { value: "holds", label: "Holds" },
  { value: "kills", label: "Kills" },
  { value: "promo_codes", label: "Promo Codes" },
  { value: "delivery", label: "Delivery" },
  // Hidden, not disabled, for a team without memberships, as on a confirm.
  ...(canAccessMemberships ? [{ value: "memberships", label: "Memberships" }] : []),
  { value: "settings", label: "Settings" },
];

const SUBTITLES = {
  multiple_shows: "Configure ticket types for each show type. Different shows can have different pricing.",
  timed_entry: "Configure ticket types for all dates in this series.",
};

const templateToTicketTypes = (template) =>
  template.ticketTypes.map((type, index) => ({
    ...blankTicketType(),
    name: type.name,
    price: String(type.price),
    inventory: type.inventory == null ? "" : String(type.inventory),
    ...(index > 0 && { availability: SALES_WINDOWS.afterEither, waitForName: template.ticketTypes[index - 1].name }),
  }));

const withNewKeys = (type) => {
  const { id, clientKey } = blankTicketType();
  return { ...type, id, clientKey };
};

const savedSetup = (template) => Boolean(template && !template.starter && template.config && Object.keys(template.config).length > 0);

const priceNamesById = (type) =>
  Object.fromEntries((type.priceOptions || []).map((option) => [option.key || option.id, `${type.name} - ${(option.label || "").trim()}`]));

const followPriceRenames = (references, before, after) => {
  const renamed = priceNamesById(after);
  const changed = Object.entries(priceNamesById(before)).filter(([id, name]) => renamed[id] !== name);
  const placeholder = (id) => `\u0000price:${id}`;
  const rename = (current, [from, to]) => ({ ...current, ...renameReferences(current, "ticketType", from, to) });
  const parked = changed.map(([id, name]) => [name, renamed[id] && placeholder(id)]).reduce(rename, references);
  const next = changed.filter(([id]) => renamed[id]).map(([id]) => [placeholder(id), renamed[id]]).reduce(rename, parked);
  return { promoCodes: next.promoCodes, memberships: next.memberships, holds: next.holds, addOns: next.addOns };
};

const DRAWER_FIELDS = [
  "availability", "startActivity", "endActivity", "waitForName", "minimumPerOrder", "maximumPerOrder",
  "visibility", "salesChannel", "settlement", "checkInLimit", "hideQrCode", "accessibleSeating",
  "presaleWindows", "pricePoints", "bundledTicketTypeName", "scanStartActivity", "scanEndActivity", "manualTaxIds",
  "manifested", "opendateDistribution", "hasFixedFees", "fixedFeeAmount", "checkInPeriod",
  "dynamicPricingEnabled", "dynamicPricingMin", "dynamicPricingMax", "shouldBeDefault", "templateId",
  "startOffsetMinutes", "endOffsetMinutes", "scanStartOffsetMinutes", "scanEndOffsetMinutes", "priceOptions",
];

const TicketConfigSection = () => {
  const { form, options, tickets, setTickets, schedule, goToStep, stepAfterTickets, ticketTemplates, setTicketTemplates, venueDefaults, ticketsLanding,
    seatingCharts, addOnSeatingCharts, seatingChartSectionsUrl, openPlanningEvent, csrfToken,
    distributionTermsAgreed, setDistributionTermsAgreed, windowNames = [], tracksOwnValues, seriesSettingsEnabled, teamId } = useSeriesEditContext();
  const locked = Boolean(tracksOwnValues);
  const [activeSubTab, setActiveSubTab] = React.useState((ticketsLanding && ticketsLanding.subTab) || "admission");
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [editingTicketId, setEditingTicketId] = React.useState(null);
  const [copyingTicket, setCopyingTicket] = React.useState(null);
  // ponytail: which template a show type came from lives only in this session, so
  // Update disappears on reload; persist it on the plan rows if people miss it.
  const [appliedTemplateIds, setAppliedTemplateIds] = React.useState({});
  const [templateNotice, setTemplateNotice] = React.useState(null);
  const [saveTemplateOpen, setSaveTemplateOpen] = React.useState(false);
  const [templateSaving, setTemplateSaving] = React.useState(false);
  const [templateError, setTemplateError] = React.useState(null);
  const [ticketLimitHit, setTicketLimitHit] = React.useState(false);
  const [activeSetKey, setActiveSetKey] = React.useState((ticketsLanding && ticketsLanding.ticketSetKey) || null);
  const { seatingMode, capacity, byShowType } = tickets;
  const seated = seatingMode === "seated" && schedule.mode !== "timed_entry";
  const summary = capacitySummary(schedule, capacity);

  const showTypes = React.useMemo(() => showTypesFor(schedule), [schedule]);
  const [activeShowType, setActiveShowType] = React.useState((ticketsLanding && ticketsLanding.showType) || showTypes[0]);

  React.useEffect(() => {
    if (showTypes.length > 0 && !showTypes.includes(activeShowType)) setActiveShowType(showTypes[0]);
  }, [showTypes, activeShowType]);

  const showType = showTypes.includes(activeShowType) ? activeShowType : showTypes[0];
  const config = configFor(byShowType, showType);
  const { started } = config;
  const timed = schedule.mode === "timed_entry";
  const sets = React.useMemo(() => (timed ? ticketSetsOf({ ticketSets: config.ticketSets }) : []), [timed, config.ticketSets]);
  const cover = React.useMemo(
    () => (timed ? coverage(schedule, sets, form.ageRestriction) : null),
    [timed, schedule, sets, form.ageRestriction],
  );
  const activeSet = timed ? sets.find((set) => set.key === activeSetKey) || sets[0] : null;
  const ticketTypes = timed ? typesInSet(config.ticketTypes, sets, activeSet.key) : config.ticketTypes;
  const templateScope = timed ? activeSet.key : showType;
  const ticketTypeNames = [...new Set(config.ticketTypes.map((type) => type.name).filter(Boolean))];
  const addOnNames = config.addOns.map((addOn) => addOn.name).filter(Boolean);
  const settings = settingsFor(config, venueDefaults);
  const membershipProducts = ((options && options.membershipProductsByVenue) || {})[form.venueOwnershipId] || [];
  const manualTaxes = ((options && options.manualTaxesByVenue) || {})[form.venueOwnershipId] || [];
  const canUseDynamicPricing = Boolean(options && options.canUseDynamicPricing);
  const selectedVenue = ((options && options.venues) || []).find((venue) => venue.value === form.venueOwnershipId);
  const dynamicPricingDefaults = (selectedVenue && selectedVenue.dynamicPricingDefaults) || {};
  const editChartUrl = options && options.ticketingSettingsUrl && form.venueOwnershipId
    ? `${options.ticketingSettingsUrl}?venue_ownership_id=${form.venueOwnershipId}`
    : null;
  const agreeToDistributionTerms = () =>
    axios
      .patch(options.agreeToOpendateDistributionTermsUrl, {}, { headers: { "X-CSRF-TOKEN": csrfToken } })
      .then(() => setDistributionTermsAgreed(true));

  const editingTicket = ticketTypes.find((type) => type.id === editingTicketId) || null;

  // WHY memoized: TicketTypeDrawer reseeds its draft whenever this prop's
  // identity changes. A fresh object literal here would do that on every parent
  // render -- so any state change in SeriesEditApp while the drawer is open
  // would silently wipe what the user had typed.
  const drawerTicket = editingTicket || copyingTicket;
  const drawerInitial = React.useMemo(
    () =>
      drawerTicket && {
        name: drawerTicket.name,
        inventory: drawerTicket.inventory,
        price: Number(drawerTicket.price) ? String(drawerTicket.price) : "",
        pricing: Number(drawerTicket.price) || (drawerTicket.sectionKey && !drawerTicket.free) ? "paid" : "free",
        description: drawerTicket.description || "",
        absorbFees: Boolean(drawerTicket.absorbFees),
        roundUp: Boolean(drawerTicket.roundUp),
        ...DRAWER_FIELDS.reduce((fields, key) => ({ ...fields, [key]: drawerTicket[key] ?? blankTicketType()[key] }), {}),
      },
    [drawerTicket],
  );

  const patch = (changes) => setTickets((current) => ({ ...current, ...changes }));

  const patchShowType = (type, changes) =>
    setTickets((current) => ({
      ...current,
      byShowType: {
        ...current.byShowType,
        [type]: { ...configFor(current.byShowType, type), ...changes },
      },
    }));

  const writeTicketTypes = (nextTypes, changes = {}) =>
    patchShowType(
      showType,
      timed ? withSetTicketTypes({ ...config, ...changes }, activeSet.key, nextTypes) : { ...changes, ticketTypes: nextTypes },
    );

  const patchSets = (change) =>
    setTickets((current) => {
      const latest = configFor(current.byShowType, showType);
      return {
        ...current,
        byShowType: { ...current.byShowType, [showType]: { ...latest, ticketSets: change(ticketSetsOf(latest)) } },
      };
    });
  const patchActiveSet = (changes) =>
    patchSets((current) => current.map((set) => (set.key === activeSet.key ? { ...set, ...changes } : set)));

  const subtitle = SUBTITLES[schedule.mode];

  if (showTypes.length === 0) {
    return (
      <div className="series-edit-card">
        <h2 className="series-edit-section-title">Ticket Configuration</h2>
        <p className="series-edit-section-subtitle">{subtitle}</p>

        <div className="series-notice warning">
          <AlertCircle width={20} height={20} aria-hidden="true" className="series-notice-icon" />
          <p className="series-notice-body">
            Generate a schedule first to configure ticket types.{" "}
            <button type="button" className="series-notice-link" onClick={() => goToStep("schedule")}>
              Go to Schedule
            </button>
          </p>
        </div>

        <StepFooter
          onBack={() => goToStep("schedule")}
          onNext={() => goToStep(stepAfterTickets)}
          nextDisabled
        />
      </div>
    );
  }

  const venueScope = {
    manualTaxIds: form.venueOwnershipId ? manualTaxes.map((tax) => tax.id) : null,
    membershipProductIds: membershipProducts.map((product) => product.id),
  };

  const fromTemplate = (current, template) =>
    savedSetup(template)
      ? withTicketTemplate(current, template.config, venueScope)
      : replaceTicketTypes(current, templateToTicketTypes(template));

  const rememberTemplate = (template, scopes) => {
    setTemplateNotice(null);
    setAppliedTemplateIds((current) =>
      scopes.reduce((next, scope) => ({ ...next, [scope]: savedSetup(template) ? template.id : null }), current),
    );
  };

  const templateTicketTypes = (template) =>
    savedSetup(template) ? applyTemplateConfig(template.config, venueScope).ticketTypes : templateToTicketTypes(template);

  const templateTicketCount = (template) =>
    savedSetup(template) ? (template.config.ticketTypes || []).length : (template.ticketTypes || []).length;

  const withinTicketTypeLimit = (added, replaced = 0) => {
    const fits = allTicketTypes(byShowType).length - replaced + added <= MAX_PLANNED_TICKET_TYPES;
    setTicketLimitHit(!fits);
    return fits;
  };

  const applyTemplate = (template) => {
    if (!withinTicketTypeLimit(templateTicketCount(template), ticketTypes.length)) return;
    const otherSetsHaveTypes = timed && config.ticketTypes.some((type) => ticketSetKeyOf(type, sets) !== activeSet.key);
    if (otherSetsHaveTypes) {
      patchShowType(showType, { ...withSetTicketTypes(config, activeSet.key, templateTicketTypes(template)), started: true });
    } else if (timed) {
      const applied = fromTemplate({ ...config, ticketTypes }, template);
      patchShowType(showType, { ...withSetTicketTypes({ ...applied, ticketTypes: config.ticketTypes }, activeSet.key, applied.ticketTypes), started: true });
    } else {
      patchShowType(showType, fromTemplate(config, template));
    }
    rememberTemplate(template, [templateScope]);
  };

  const applyTemplateEverywhere = (template) => {
    const targets = showTypes.filter((type) => type === showType || !configFor(byShowType, type).started);
    const replaced = targets.reduce((count, type) => count + configFor(byShowType, type).ticketTypes.length, 0);
    if (!withinTicketTypeLimit(targets.length * templateTicketCount(template), replaced)) return;
    setTickets((current) => ({
      ...current,
      byShowType: targets.reduce(
        (next, type) => ({ ...next, [type]: fromTemplate(configFor(current.byShowType, type), template) }),
        { ...current.byShowType },
      ),
    }));
    rememberTemplate(template, targets);
  };

  const appliedTemplate = ticketTemplates.find((template) => template.id === appliedTemplateIds[templateScope]);

  const sendTemplate = (request, notice) => {
    setTemplateSaving(true);
    setTemplateError(null);
    return request
      .then(({ data }) => {
        setTicketTemplates((current) => [...current.filter((template) => template.id !== data.id), data]);
        setAppliedTemplateIds((current) => ({ ...current, [templateScope]: data.id }));
        setTemplateNotice({ scope: templateScope, text: notice(data) });
        setSaveTemplateOpen(false);
      })
      .catch((error) => {
        const errors = error && error.response && error.response.data && error.response.data.errors;
        setTemplateError(errors ? errors.join(", ") : "Couldn't save this template. Try again.");
      })
      .finally(() => setTemplateSaving(false));
  };

  const scopeSuffix = timed ? (sets.length > 1 ? ` · ${activeSet.name}` : "") : showTypes.length > 1 ? ` · ${showType}` : "";

  const saveTemplate = ({ name, description }) =>
    sendTemplate(
      axios.post(
        options.ticketTemplatesUrl,
        {
          ticket_type_template_set: {
            name,
            description: description || `Saved from ${(form.name || "").trim() || "a series"}${scopeSuffix}`,
            config_json: JSON.stringify(templateSnapshot({ ...config, ticketTypes })),
          },
        },
        { headers: { "X-CSRF-TOKEN": csrfToken } },
      ),
      (data) => `Saved "${data.name}". It's now in your ticket templates.`,
    );

  const updateTemplate = () =>
    sendTemplate(
      axios.patch(
        `${options.ticketTemplatesUrl}/${appliedTemplate.id}`,
        { ticket_type_template_set: { config_json: JSON.stringify(templateSnapshot({ ...config, ticketTypes })) } },
        { headers: { "X-CSRF-TOKEN": csrfToken } },
      ),
      (data) => `Updated "${data.name}" with these settings.`,
    );

  const copyFrom = (other) => {
    if (timed) {
      const source = sets.find((set) => set.name === other);
      const copies = typesInSet(config.ticketTypes, sets, source.key);
      if (!withinTicketTypeLimit(copies.length)) return;
      writeTicketTypes(
        [...ticketTypes, ...copies.map(withNewKeys)],
        { started: true },
      );
      return;
    }
    if (!withinTicketTypeLimit(configFor(byShowType, other).ticketTypes.length, config.ticketTypes.length)) return;
    patchShowType(
      showType,
      replaceTicketTypes(
        config,
        configFor(byShowType, other).ticketTypes.map(withNewKeys),
      ),
    );
  };

  const beginEditTicket = (type) => {
    setEditingTicketId(type.id);
    setDrawerOpen(true);
  };

  // A copy opens as a new ticket, as on a confirm; its pre-sale windows follow the new name.
  const startNewTicket = () => {
    if (withinTicketTypeLimit(1)) setDrawerOpen(true);
  };

  const beginCopyTicket = (type) => {
    if (!withinTicketTypeLimit(1)) return;
    const name = copyName(type.name, ticketTypes.flatMap((other) => [other.name, ...(other.presaleWindows || []).map((window) => window.name)]));
    setCopyingTicket({
      ...type,
      name,
      shouldBeDefault: false,
      templateId: null,
      presaleWindows: (type.presaleWindows || []).map((window) => ({
        ...window,
        id: blankPresaleWindow().id,
        name: followTicketName(window.name, type.name.trim(), name),
      })),
    });
    setDrawerOpen(true);
  };

  const membershipNameFor = (id) => (membershipProducts.find((product) => String(product.id) === String(id)) || {}).name || "a membership discount";

  const deleteTicket = (type) => {
    const references = ticketTypeReferences(config, config.ticketTypes.filter((candidate) => candidate.id !== type.id), type.name);
    const blocked = deletionBlockedMessage(config, references, type.name, membershipNameFor);
    if (blocked) {
      toastError(blocked);
      return;
    }
    if (timed) {
      patchShowType(showType, withoutTicketType(config, type.id));
    } else {
      patchShowType(showType, {
        ticketTypes: swapTicketReferences(ticketTypes.filter((candidate) => candidate.id !== type.id), type.name),
        ...references,
      });
    }
  };

  const ticketList = (empty) => (
    <SeriesTicketList
      ticketTypes={ticketTypes}
      schedule={schedule}
      empty={empty}
      onEdit={beginEditTicket}
      onCopy={beginCopyTicket}
      onDelete={deleteTicket}
      onMove={(from, to) => writeTicketTypes(moveRow(ticketTypes, from, to))}
    />
  );

  const copyableShowTypes = timed
    ? sets.filter((set) => set.key !== activeSet.key && typesInSet(config.ticketTypes, sets, set.key).length > 0).map((set) => set.name)
    : showTypes.filter((other) => other !== showType && configFor(byShowType, other).ticketTypes.length > 0);

  const lowestPrice = ticketTypes
    .map((type) => Number(type.price) || 0)
    .filter((price) => price > 0)
    .sort((a, b) => a - b)[0];

  const totalTicketTypes = allTicketTypes(byShowType).length;
  const atTicketTypeLimit = totalTicketTypes >= MAX_PLANNED_TICKET_TYPES;
  const ticketLimitNotice = (ticketLimitHit || atTicketTypeLimit) && (
    <p className="series-schedule-error" role="alert" data-testid="ticket-type-limit">
      A series can have at most {MAX_PLANNED_TICKET_TYPES} ticket types.
    </p>
  );
  const picker = (scopeLabel, scopeCount, applyToAll) => (
    <>
      {ticketLimitNotice}
      <TicketTemplatePicker
        templates={ticketTemplates}
        showType={scopeLabel}
        showTypeCount={scopeCount}
        copyableShowTypes={copyableShowTypes}
        onApply={applyTemplate}
        onApplyToAll={applyToAll}
        onCopyFrom={copyFrom}
        onStartFromScratch={startNewTicket}
      />
    </>
  );
  const showsSetPicker = timed && !seated && ticketTypes.length === 0 && (ticketTemplates.length > 0 || copyableShowTypes.length > 0);
  const seatingChart = seated ? seatingCharts.find((chart) => chart.id === config.seatingChartId) : null;
  const addOnCharts = addOnSeatingCharts;
  const addOnChart = addOnCharts.find((chart) => chart.id === config.addOnSeatingChartId);
  const admissionChartEntry = {
    context: null,
    label: seated ? `Admission: ${seatingChart ? seatingChart.name : "no chart yet"}` : "Admission",
    name: seated ? (seatingChart ? seatingChart.name : "no chart yet") : null,
    open: seated && config.seatingChartId ? () => openPlanningEvent(showType, config.seatingChartId) : undefined,
  };
  const addOnChartEntry = addOnChart && {
    context: ADD_ON_CHART,
    label: `Add-on: ${addOnChart.name}`,
    name: addOnChart.name,
    open: () => openPlanningEvent(showType, addOnChart.id, ADD_ON_CHART),
  };
  const holdCharts = [admissionChartEntry, addOnChartEntry].filter(Boolean);
  const killCharts = [seated && admissionChartEntry, addOnChartEntry].filter(Boolean);
  const revealableHoldNames = seated ? config.holds.filter((hold) => !chartContextOf(hold) && hold.objects).map((hold) => hold.name) : [];
  const chartsKey = `${showType}-${config.seatingChartId || "ga"}-${(addOnChart && addOnChart.id) || "none"}`;
  const patchLatest = (change) =>
    setTickets((current) => ({
      ...current,
      byShowType: { ...current.byShowType, [showType]: change(configFor(current.byShowType, showType)) },
    }));
  const seatsPerShow = ticketTypes.reduce((seats, type) => seats + (Number(type.inventory) || 0), 0);

  return (
    <div className="series-edit-card">
      <h2 className="series-edit-section-title">Ticket Configuration</h2>
      <p className="series-edit-section-subtitle">{subtitle}</p>

      <section className="series-step-panel">
        {schedule.mode === "timed_entry" ? (
          <div className="series-step-panel-body series-seating-row" data-testid="timed-entry-seating-note">
            <span className="series-edit-label series-edit-label-flush">Seating</span>
            <Badge>General Admission</Badge>
            <span className="series-edit-hint">
              Timed entry series are general admission. Need assigned seats? Use Multiple Shows instead.
            </span>
            <button type="button" className="series-notice-link" onClick={() => goToStep("schedule")}>
              Go to Schedule
            </button>
          </div>
        ) : (
          <div className="series-step-panel-body">
            <PublishedLock locked={locked}>
              <Tabs
                variant="button-border"
                size="sm"
                collapseBelow={0}
                aria-label="Seating mode"
                items={SEATING_MODES}
                value={seatingMode}
                onChange={(mode) => !locked && seatingMode !== mode && setTickets((current) => withSeatingMode(current, mode))}
              />
            </PublishedLock>
          </div>
        )}
      </section>
      {locked && <PublishedLockNote />}

      {timed ? (
        <div className="series-notice">
          <InfoCircle width={20} height={20} aria-hidden="true" className="series-notice-icon" />
          <div>
            <div className="series-notice-title">
              {sets.length > 1
                ? `${sets.length} ticket sets across ${cover.scheduled.length} date${cover.scheduled.length === 1 ? "" : "s"}`
                : "One set of ticket types for all dates"}
            </div>
            <div className="series-notice-body">
              Use one ticket set for every date, or add sets for specific days of the week or dates (like weekend pricing
              or a 21+ night). Specific dates override monthly patterns, which override days of the week, which override
              the default set.
            </div>
          </div>
        </div>
      ) : (
        <div className="series-notice">
          <InfoCircle width={20} height={20} aria-hidden="true" className="series-notice-icon" />
          <div>
            <div className="series-notice-title">
              {showTypes.length} show {showTypes.length === 1 ? "type" : "types"} &mdash; each gets its own
              ticket configuration
            </div>
            <div className="series-notice-body">
              Show types come from your schedule labels. Each type can have different ticket types,
              pricing, and capacity.
            </div>
          </div>
        </div>
      )}

      {showTypes.length > 1 && (
        <Tabs
          aria-label="Show types"
          collapseBelow={0}
          value={showType}
          items={showTypes.map((type) => ({
            value: type,
            label: type,
            count: configFor(byShowType, type).ticketTypes.length,
          }))}
          onChange={setActiveShowType}
        />
      )}

      {drawerOpen ? (
        <div className="series-ticket-drawer-placeholder" />
      ) : !timed && !seated && !started && (ticketTemplates.length > 0 || copyableShowTypes.length > 0) ? (
        picker(showType, showTypes.length, applyTemplateEverywhere)
      ) : (
        <div className="series-ticket-config">
          {!seated && options && options.ticketTemplatesUrl && (
            <div className="series-template-actions">
              {templateNotice && templateNotice.scope === templateScope && (
                <span className="series-template-notice">
                  <CheckCircle width={14} height={14} aria-hidden="true" /> {templateNotice.text}
                </span>
              )}
              {templateError && !saveTemplateOpen && <span className="series-template-error">{templateError}</span>}
              {appliedTemplate && (
                <Button variant="tertiary" size="sm" disabled={templateSaving || ticketTypes.length === 0} onClick={updateTemplate}>
                  {`Update "${appliedTemplate.name}"`}
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                disabled={ticketTypes.length === 0}
                onClick={() => {
                  setTemplateError(null);
                  setSaveTemplateOpen(true);
                }}
              >
                Save as Template
              </Button>
            </div>
          )}
          <Tabs
            aria-label="Ticket configuration sections"
            collapseBelow={0}
            value={activeSubTab}
            items={subTabs(options || {})}
            onChange={setActiveSubTab}
          />

          {activeSubTab === "add_ons" && (
            <AddOnsTab
              addOns={config.addOns}
              ticketTypeNames={ticketTypeNames}
              showActivityNames={windowNames}
              manualTaxes={manualTaxes}
              canUseDynamicPricing={canUseDynamicPricing}
              dynamicPricingDefault={Boolean(dynamicPricingDefaults.addOns)}
              settings={settings}
              addOnsCopyDefaults={(options && options.addOnsCopyDefaults) || {}}
              messageTemplates={(options && options.addOnMessageTemplates) || undefined}
              messageTemplatesUrl={options && options.addOnMessageTemplatesUrl}
              onSettingsChange={(changes) => patchShowType(showType, { settings: { ...settings, ...changes } })}
              addOnCharts={addOnCharts}
              addOnSeatingChartId={addOnChart ? addOnChart.id : undefined}
              sectionsUrl={seatingChartSectionsUrl}
              editChartUrl={editChartUrl}
              showTypeLabel={showTypes.length > 1 ? showType : null}
              onChooseVenue={() => goToStep("details")}
              timedEntry={schedule.mode === "timed_entry"}
              defaultCheckInPeriod={settings.checkInPeriod || "all_time"}
              feeQuoteUrl={options && options.addOnFeeQuoteUrl}
              venueOwnershipId={form.venueOwnershipId}
              csrfToken={csrfToken}
              onPickChart={(chartId, sections) => patchLatest((latest) => withAddOnChart(latest, chartId, sections))}
              onRemoveChart={() => patchLatest((latest) => withAddOnChart(latest, null))}
              onChange={(addOns) => {
                const removed = config.addOns.filter((addOn) => !addOns.some((kept) => kept.id === addOn.id));
                const renamed = addOns
                  .map((addOn) => [config.addOns.find((before) => before.id === addOn.id), addOn])
                  .filter(([before, after]) => before && before.name !== after.name);
                let next = { ...config, addOns };
                removed.forEach((addOn) => (next = { ...next, ...renameReferences(next, "addOn", addOn.name) }));
                renamed.forEach(([before, after]) => (next = { ...next, ...renameReferences(next, "addOn", before.name, after.name) }));
                patchShowType(showType, { ...next, addOns });
              }}
            />
          )}

          {activeSubTab === "holds" && (
            <HoldsTab
              key={chartsKey}
              seated={seated}
              charts={holdCharts}
              holds={config.holds}
              kills={config.kills}
              ticketTypeNames={ticketTypeNames}
              timedEntry={schedule.mode === "timed_entry"}
              onChange={(holds) => {
                const admissionHolds = (list) => list.filter((hold) => !chartContextOf(hold));
                const before = admissionHolds(config.holds);
                const removed = before.filter((hold) => !holds.some((kept) => kept.id === hold.id));
                const renamed = admissionHolds(holds)
                  .map((hold) => [before.find((previous) => previous.id === hold.id), hold])
                  .filter(([previous, after]) => previous && previous.name !== after.name);
                let next = { ...config, holds };
                removed.forEach((hold) => (next = { ...next, ...renameReferences(next, "hold", hold.name) }));
                renamed.forEach(([previous, after]) => (next = { ...next, ...renameReferences(next, "hold", previous.name, after.name) }));
                patchShowType(showType, next);
              }}
            />
          )}

          {activeSubTab === "kills" && (
            <KillsTab
              key={chartsKey}
              charts={killCharts}
              kills={config.kills}
              holds={config.holds}
              onChange={(kills) => patchShowType(showType, { kills })}
            />
          )}

          {activeSubTab === "promo_codes" && (
            <PromoCodesTab
              promoCodes={config.promoCodes}
              ticketTypeNames={ticketTypeNames}
              holdNames={revealableHoldNames}
              showActivityNames={windowNames}
              onChange={(promoCodes) => patchShowType(showType, { promoCodes })}
            />
          )}

          {activeSubTab === "delivery" && (
            <DeliveryTab
              settings={settings}
              onChange={(changes) => patchShowType(showType, { settings: { ...settings, ...changes } })}
            />
          )}

          {activeSubTab === "memberships" && (
            <MembershipsTab
              products={membershipProducts}
              memberships={config.memberships}
              ticketTypeNames={ticketTypeNames}
              addOnNames={addOnNames}
              onChange={(memberships) => patchShowType(showType, { memberships })}
            />
          )}

          {activeSubTab === "settings" && (
            <>
              <SettingsTab
                key={showType}
                settings={settings}
                canEnableCheckoutCaptcha={Boolean(options && options.canEnableCheckoutCaptcha)}
                onChange={(changes) => patchShowType(showType, { settings: { ...settings, ...changes } })}
              />
              {/* Fees live on the Series Settings step, which needs the wizard flag. */}
              {!seriesSettingsEnabled && (
                <SeriesFeeSettings
                  csrfToken={csrfToken}
                  teamId={teamId}
                  venueOwnershipId={form.venueOwnershipId}
                  fees={tickets.fees}
                  onChange={(fees) => setTickets((current) => ({ ...current, fees }))}
                  canAccessGeneralLedgerCodes={Boolean(options && options.canAccessGeneralLedgerCodes)}
                  feeRuleSets={(selectedVenue && selectedVenue.feeRuleSets) || []}
                />
              )}
            </>
          )}

          {activeSubTab === "admission" && seated && (
            <div className="series-seated-admission">
              <SeatingChartPicker
                charts={seatingCharts}
                value={config.seatingChartId}
                sectionsUrl={seatingChartSectionsUrl}
                editChartUrl={editChartUrl}
                showTypeLabel={showTypes.length > 1 ? showType : null}
                onChooseVenue={() => goToStep("details")}
                onPick={(seatingChartId, sections) =>
                  setTickets((current) => {
                    const latest = configFor(current.byShowType, showType);
                    const swapped = latest.seatingChartId !== seatingChartId;
                    let next = replaceTicketTypes(latest, sectionTicketTypes(sections, swapped ? [] : latest.ticketTypes));
                    if (swapped) {
                      (next.holds || []).filter((hold) => !chartContextOf(hold)).forEach((hold) => (next = { ...next, ...renameReferences(next, "hold", hold.name) }));
                      next = {
                        ...next,
                        holds: (next.holds || []).filter((hold) => chartContextOf(hold)),
                        kills: (next.kills || []).filter((kill) => chartContextOf(kill)),
                      };
                    }
                    return {
                      ...current,
                      byShowType: {
                        ...current.byShowType,
                        [showType]: { ...next, seatingChartId },
                      },
                    };
                  })
                }
              />
              {seatingChart && ticketTypes.length > 0 && (
                <section className="series-seated-sections">
                  {ticketList("No sections yet.")}
                  <p className="series-edit-hint">Prices can be adjusted per event after the series is published.</p>
                </section>
              )}
            </div>
          )}

          {activeSubTab === "admission" && !seated && (
          <>
          {timed && (
            <div className="series-ticket-sets">
              <TicketSetTabs
                sets={sets}
                activeKey={activeSet.key}
                typeCounts={Object.fromEntries(sets.map((set) => [set.key, typesInSet(config.ticketTypes, sets, set.key).length]))}
                onSelect={setActiveSetKey}
                locked={locked}
                onAdd={() => {
                  const created = newTicketSet(sets);
                  patchSets((current) => [...current, created]);
                  setActiveSetKey(created.key);
                }}
              />
              <TicketSetSettings
                set={activeSet}
                sets={sets}
                deletable={!locked && activeSet.key !== sets[0].key}
                locked={locked}
                matchedDates={cover.dateCounts[activeSet.key] || 0}
                scheduledDates={cover.scheduled.length}
                scheduledWeekdays={[...new Set(cover.scheduled.map((date) => new Date(`${date}T12:00:00`).getDay()))]}
                times={cover.times}
                seriesAge={form.ageRestriction || ""}
                ageOptions={(options && options.ageRestrictions) || []}
                samplePaid={ticketTypes.find((type) => Number(type.price) > 0)}
                onChange={patchActiveSet}
                onRename={(name) => patchActiveSet({ name })}
                onDelete={() => {
                  patchShowType(showType, withoutTicketSetConfig(config, activeSet.key));
                  setActiveSetKey(null);
                }}
              />
              {cover.scheduled.length > 0 && (
                <DateCoverageCalendar
                  sets={sets}
                  activeSet={activeSet}
                  cover={cover}
                  ticketTypes={config.ticketTypes}
                  decisions={decisionsOf(sets)}
                  locked={locked}
                  onToggleDate={(date) =>
                    patchSets((current) =>
                      current.map((set) => {
                        if (set.key !== activeSet.key) return set;
                        const dates = set.dates || [];
                        return { ...set, dates: dates.includes(date) ? dates.filter((each) => each !== date) : [...dates, date].sort() };
                      }),
                    )
                  }
                  onJumpToSet={setActiveSetKey}
                  onDecide={(winner, loser) => patchSets((current) => decideOverlap(current, winner, loser))}
                  onClearDecision={(winner, loser) => patchSets((current) => clearDecision(current, winner, loser))}
                />
              )}
              {cover.times.length >= 2 &&
                sets.some((set) => set.timeMode === "some" || (set.priceAdjustments || []).length > 0) && (
                  <WeeklyEntryTimes sets={sets} cover={cover} onJumpToSet={setActiveSetKey} />
                )}
            </div>
          )}
          <div className="series-ticket-toolbar">
            {schedule.mode === "timed_entry" ? (
              <p className="series-edit-hint series-capacity-field" data-testid="timed-entry-capacity-note">
                Each entry time admits {schedule.entry.capacity || "—"} guests, set on the Schedule step.
              </p>
            ) : (
              <label className="series-show-field series-capacity-field">
                <span className="series-edit-label series-edit-label-flush">Capacity</span>
                <input
                  type="number"
                  min="0"
                  className="series-edit-input"
                  aria-label="Capacity"
                  disabled={locked}
                  placeholder={form.venueName ? "" : "Set a venue to fill this"}
                  value={capacity}
                  onChange={(event) => patch({ capacity: event.target.value })}
                />
                {!form.venueName && (
                  <button type="button" className="series-notice-link" onClick={() => goToStep("details")}>
                    Choose a venue
                  </button>
                )}
              </label>
            )}
            <div className="series-ticket-toolbar-actions">
              <Button variant="secondary" icon={Plus} disabled={atTicketTypeLimit} onClick={startNewTicket}>
                Add Ticket
              </Button>
            </div>
          </div>
          {!showsSetPicker && ticketLimitNotice}

          <div className="series-ticket-group-header">{timed ? activeSet.name : showType}</div>

          {showsSetPicker ? picker(activeSet.name, sets.length) : ticketList("No ticket types yet.")}
          </>
          )}

          <p className="series-generated-summary">
            {(timed
              ? sets.map((set) => `${set.name}: ${typesInSet(config.ticketTypes, sets, set.key).length}`)
              : showTypes.map((type) => `${type}: ${configFor(byShowType, type).ticketTypes.length}`)
            ).join(" · ")}
            {` · ${totalTicketTypes} total ticket ${totalTicketTypes === 1 ? "type" : "types"}`}
            {seated ? seatsPerShow > 0 && ` · ${seatsPerShow} seats per show` : summary && ` · ${summary}`}
            {lowestPrice && ` · From $${lowestPrice}`}
          </p>
        </div>
      )}

      <StepFooter onBack={() => goToStep("schedule")} onNext={() => goToStep(stepAfterTickets)} />

      <SaveTicketTemplateModal
        open={saveTemplateOpen}
        config={templateSnapshot({ ...config, ticketTypes })}
        settings={settings}
        membershipProducts={membershipProducts}
        takenNames={ticketTemplates.map((template) => template.name)}
        saving={templateSaving}
        error={templateError}
        onSave={saveTemplate}
        onClose={() => setSaveTemplateOpen(false)}
      />

      <TicketTypeDrawer
        open={drawerOpen}
        initial={drawerInitial}
        takenNames={ticketTypes
          .filter((type) => type.id !== editingTicketId)
          .flatMap((type) => [type.name, ...(type.presaleWindows || []).map((window) => window.name)])
          .map((name) => (name || "").trim())}
        seated={Boolean(editingTicket && editingTicket.sectionKey)}
        timedEntry={schedule.mode === "timed_entry"}
        showActivityNames={windowNames}
        otherTickets={ticketTypes.filter((type) => type.name && (!editingTicket || type.id !== editingTicket.id))}
        defaultCheckInPeriod={settings.checkInPeriod || "all_time"}
        feeQuoteUrl={options && options.ticketFeeQuoteUrl}
        venueOwnershipId={form.venueOwnershipId}
        csrfToken={csrfToken}
        manualTaxes={manualTaxes}
        canUseDynamicPricing={canUseDynamicPricing}
        dynamicPricingDefault={Boolean(dynamicPricingDefaults.ticketTypes)}
        allowFixedFees={Boolean(options && options.allowFixedFees)}
        distributionTermsAgreed={distributionTermsAgreed}
        onAgreeToDistributionTerms={agreeToDistributionTerms}
        inventoryHint={
          editingTicket && editingTicket.sectionKey
            ? "Set by the seating chart's section."
            : schedule.mode === "timed_entry"
            ? "Leave blank to sell from the entry time's shared pool."
            : "Leave blank to sell from the show's capacity."
        }
        editing={Boolean(editingTicket)}
        onCancel={() => {
          setDrawerOpen(false);
          setEditingTicketId(null);
          setCopyingTicket(null);
        }}
        onSave={(draft) => {
          const fields = {
            name: draft.name.trim(),
            price: draft.pricing === "free" ? "0" : draft.price,
            inventory: draft.inventory,
            description: draft.description,
            absorbFees: draft.pricing === "paid" && draft.absorbFees,
            roundUp: draft.pricing === "paid" && draft.roundUp,
            free: draft.pricing === "free",
            ...DRAWER_FIELDS.reduce((picked, key) => ({ ...picked, [key]: draft[key] }), {}),
            settlement: draft.pricing === "free" ? "" : draft.settlement,
            pricePoints: draft.pricing === "free" ? [] : draft.pricePoints,
          };
          const saved = editingTicket
            ? ticketTypes.map((type) => (type.id === editingTicket.id ? { ...type, ...fields } : type))
            : [...ticketTypes, { ...blankTicketType(), ...fields }];
          const others = config.ticketTypes.filter((type) => !editingTicket || type.id !== editingTicket.id);
          const references = editingTicket && followPriceRenames(
            { ...config, ...ticketTypeReferences(config, others, editingTicket.name, fields.name) },
            { ...editingTicket, name: fields.name },
            { ...editingTicket, ...fields },
          );
          const emptied = references ? emptiedByRemoval(config, references, membershipNameFor) : [];
          if (emptied.length > 0) {
            const labelsById = Object.fromEntries((editingTicket.priceOptions || []).map((option) => [option.key || option.id, option.label]));
            const blockedBy = (row) => Object.entries(priceNamesById(editingTicket)).filter(([, name]) => row.names.includes(name)).map(([id]) => labelsById[id]);
            const blocked = [...new Set(emptied.flatMap(blockedBy))];
            toastError(`Can't remove ${blocked.join(", ")}: ${emptied.map((row) => `${row.name} applies only to ${blockedBy(row).join(", ")}`).join("; ")}. Change ${emptied.length > 1 ? "those" : "that"} first.`);
            return;
          }
          writeTicketTypes(editingTicket ? swapTicketReferences(saved, editingTicket.name, fields.name) : saved, {
            started: true,
            ...references,
          });
          setDrawerOpen(false);
          setEditingTicketId(null);
          setCopyingTicket(null);
        }}
      />
    </div>
  );
};

TicketConfigSection.propTypes = {};

export default TicketConfigSection;
